import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';

import { jobs } from '../api/client';
import type { RunDetailView } from '../api/types';
import { formatBytes, formatDuration, formatTsFull, shortSha, shortenPath } from '../lib/format';
import { Badge, EmptyState, ErrorState, Spinner, StatusPill } from '../components/ui';

interface EventRow {
  seq: string;
  ev: string;
  ts: string;
  target?: string;
  status?: string;
  size_bytes?: number;
  path?: string;
  sha256?: string;
  kz?: string;
  duration_s?: number;
  skip_reason?: string;
  note?: string;
  deleted_keys?: string[];
  deleted_count?: number;
  kept_count?: number;
  expected_targets?: string[];
  uploaded?: number;
  skipped?: number;
  failed?: number;
  deleted?: number;
}

function readEvent(raw: Record<string, unknown>): EventRow {
  return raw as unknown as EventRow;
}

/** Marker colour follows the meaning, not the event kind. */
function markerOf(event: EventRow): string {
  if (event.ev === 'run_end') return event.status === 'ok' ? 'timeline__marker--ok' : 'timeline__marker--bad';
  if (event.ev === 'skip') return 'timeline__marker--warn';
  if (event.status === 'fail') return 'timeline__marker--bad';
  return 'timeline__marker--ok';
}

function eventTitle(event: EventRow): string {
  switch (event.ev) {
    case 'run_start':
      return `run_start · ожидается ${event.expected_targets?.length ?? 0} таргет(ов)`;
    case 'artifact':
      return event.target ?? 'artifact';
    case 'skip':
      return `${event.target ?? 'skip'} · skip`;
    case 'rotation':
      return `${event.target ?? 'rotation'} · rotation`;
    case 'run_end':
      return 'run_end · счётчики';
    default:
      return event.ev;
  }
}

function eventBody(event: EventRow): string {
  const parts: string[] = [];
  if (event.size_bytes !== undefined) parts.push(formatBytes(event.size_bytes));
  if (event.sha256) parts.push(`sha ${shortSha(event.sha256)}`);
  if (event.kz) parts.push(`kz ${event.kz}`);
  if (event.duration_s !== undefined) parts.push(formatDuration(event.duration_s));
  if (event.skip_reason) parts.push(`причина: ${event.skip_reason}`);
  if (event.note) parts.push(event.note);
  if (event.ev === 'run_end') {
    parts.push(`uploaded ${event.uploaded ?? 0}, skipped ${event.skipped ?? 0}, failed ${event.failed ?? 0}, deleted ${event.deleted ?? 0}`);
  }
  return parts.join(' · ');
}

/** One event, vertically on the timeline. */
export function TimelineItem({ event }: { event: EventRow }) {
  const rotationKeys = event.ev === 'rotation' ? (event.deleted_keys ?? []) : [];
  return (
    <li className="timeline__item">
      <span className={`timeline__marker ${markerOf(event)}`} aria-hidden="true" />
      <div className="timeline__head">
        <span className="timeline__target">{eventTitle(event)}</span>
        {event.kz ? (
          <Badge tone={event.kz === 'ok' ? 'ok' : event.kz === 'fail' ? 'bad' : 'neutral'}>kz {event.kz}</Badge>
        ) : null}
        {event.status === 'fail' ? <Badge tone="bad">fail</Badge> : null}
        <span className="cell-muted" style={{ fontSize: 12 }}>
          {formatTsFull(event.ts)} · seq {event.seq}
        </span>
      </div>
      <div className="timeline__body">
        {eventBody(event)}
        {event.path ? (
          <>
            {' '}
            <span title={event.path}>{shortenPath(event.path, 60)}</span>
          </>
        ) : null}
      </div>
      {rotationKeys.length > 0 ? (
        <details>
          <summary>
            удалено ключей: {event.deleted_count ?? rotationKeys.length}
            {event.kept_count !== undefined ? ` · оставлено ${event.kept_count}` : ''}
          </summary>
          <pre>{rotationKeys.join('\n')}</pre>
        </details>
      ) : null}
    </li>
  );
}

/** Warnings first (SPEC PLAN W3.3), then the grouped event stream. */
export function RunTimeline({ data }: { data: RunDetailView }) {
  const events = data.events.map(readEvent);
  const grouped = new Map<string, EventRow[]>();
  for (const event of events) {
    const key = event.target ?? (event.ev === 'run_start' || event.ev === 'run_end' ? '' : '—');
    const list = grouped.get(key) ?? [];
    list.push(event);
    grouped.set(key, list);
  }

  return (
    <div data-testid="run-timeline">
      {data.warnings.map((warning) => (
        <p key={warning} className="banner banner--bad" role="alert">
          <strong>Внимание.</strong> {warning}
        </p>
      ))}

      <div className="timeline">
        {events.length === 0 ? (
          <EmptyState title="Событий нет" hint="Сырой лог события истёк (TTL 90 дней) или джоба ещё не отчиталась." />
        ) : null}
        {[...grouped.entries()].map(([target, group]) => (
          <div key={target || '_run'}>
            {target ? (
              <h3 className="section-title" style={{ marginBottom: 'var(--space-2)' }}>
                {target}
              </h3>
            ) : null}
            <ul className="timeline">
              {group.map((event, index) => (
                <TimelineItem key={`${event.seq}-${index}`} event={event} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <details className="raw-events" data-testid="raw-events">
        <summary>Сырые события ({events.length})</summary>
        <pre>{JSON.stringify(data.events, null, 2)}</pre>
      </details>
    </div>
  );
}

/** `/jobs/:id/runs/:run` — the one-run forensic view. */
export function RunTimelinePage() {
  const { id = '', run = '' } = useParams();
  const query = useQuery({
    queryKey: ['run', id, run],
    queryFn: ({ signal }) => jobs.run(id, run, signal),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });

  if (query.isPending) return <Spinner label={`Загружаю прогон ${run}`} />;
  if (query.isError) return <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} />;
  if (!query.data) return <ErrorState message={`Прогон «${run}» не найден`} />;

  const data = query.data;
  const durationDelta =
    data.duration_s !== null && data.prev?.duration_s != null ? data.duration_s - data.prev.duration_s : null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 style={{ overflowWrap: 'anywhere' }}>{data.run}</h1>
          <p className="page-header__sub">
            <Link to={`/jobs/${data.job}`}>← {data.job}</Link> · <StatusPill status={data.status} />{' '}
            <span className="muted">
              {formatTsFull(data.started)} → {formatTsFull(data.ended)} · {formatDuration(data.duration_s)}
              {durationDelta !== null ? (
                <>
                  {' '}
                  (пред. {formatDuration(data.prev?.duration_s)}, {durationDelta > 0 ? '+' : ''}
                  {durationDelta} с)
                </>
              ) : null}
            </span>
          </p>
        </div>
        <div className="muted" style={{ textAlign: 'right', fontSize: 13 }}>
          expected {data.expected.length} · got{' '}
          {(data.counters?.uploaded ?? 0) + (data.counters?.skipped ?? 0) + (data.counters?.failed ?? 0)}
          {data.missing.length > 0 ? ` · нет события: ${data.missing.length}` : ''}
        </div>
      </div>

      <RunTimeline data={data} />
    </>
  );
}
